import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["schedulesContainer", "duedateContainer"]
  
  connect() {
    this.initDeleteButtons()
    this.initExistingSchedules() // New method to initialize existing schedules
    document.getElementById("add-schedule-button")?.addEventListener("click", this.addNewSchedule.bind(this))
    this.checkTaskStatus()
    
    // Listen for schedule changes
    this.observeScheduleChanges()
  }
  
  disconnect() {
    document.getElementById("add-schedule-button")?.removeEventListener("click", this.addNewSchedule.bind(this))
  }
  
  // Check task status and disable form fields accordingly
  checkTaskStatus() {
    const statusSelect = document.querySelector('select[name="task[status]"]')
    const hasSchedules = document.querySelectorAll('.task-schedule-container:not(.d-none)').length > 0
    
    // Check if this is an existing task (has an ID) or a new task
    const taskId = this.element.dataset.taskId
    const isExistingTask = taskId && taskId !== ''
    
    // Disable fields only if:
    // 1. Task is scheduled, OR
    // 2. Task has schedules AND it's an existing task (not a new task being created)
    if ((statusSelect && statusSelect.value === 'scheduled') || 
        (hasSchedules && isExistingTask)) {
      this.disableMainFormFields()
      this.hideDuedateField()
    }
    
    // Add status change listener
    if (statusSelect) {
      statusSelect.addEventListener('change', (e) => {
        if (e.target.value === 'scheduled') {
          this.disableMainFormFields()
          this.hideDuedateField()
        } else if (!hasSchedules || !isExistingTask) {
          this.enableMainFormFields()
          this.showDuedateField()
        }
      })
    }
  }
  
  // Disable task form fields
  disableMainFormFields() {
    const fieldsToDisable = [
      'input[name="task[name]"]',
      'select[name="task[responsible_id]"]',
      'select[name="task[costbearer]"]',
      'input[name="task[duedate]"]',
      'select[name="task[priority]"]',
      'select[name="task[type_id]"]',
      'select[name="task[tag_ids][]"]',
      'div[data-trix-editor]'
    ]
    
    fieldsToDisable.forEach(selector => {
      const field = document.querySelector(selector)
      if (field) field.setAttribute('disabled', 'disabled')
    })
    
    // Külön kezelés a Trix editornak
    const trixEditor = document.querySelector("trix-editor")
    if (trixEditor) trixEditor.setAttribute('disabled', 'disabled')
  }
  
  // Enable task form fields
  enableMainFormFields() {
    const fieldsToEnable = [
      'input[name="task[name]"]',
      'select[name="task[responsible_id]"]',
      'select[name="task[costbearer]"]',
      'input[name="task[duedate]"]',
      'select[name="task[priority]"]',
      'select[name="task[type_id]"]',
      'select[name="task[tag_ids][]"]',
      'div[data-trix-editor]'
    ]
    
    fieldsToEnable.forEach(selector => {
      const field = document.querySelector(selector)
      if (field) field.removeAttribute('disabled')
    })
    
    // Külön kezelés a Trix editornak
    const trixEditor = document.querySelector("trix-editor")
    if (trixEditor) trixEditor.removeAttribute('disabled')
  }
  
  // Hide due date field and set value to null
  hideDuedateField() {
    if (this.hasDuedateContainerTarget) {
      this.duedateContainerTarget.style.display = 'none'
      const duedateInput = this.duedateContainerTarget.querySelector('input[name="task[duedate]"]')
      if (duedateInput) {
        duedateInput.value = ''
      }
    }
  }
  
  // Show due date field
  showDuedateField() {
    if (this.hasDuedateContainerTarget) {
      this.duedateContainerTarget.style.display = 'block'
    }
  }
  
  // Observe schedule changes and update due date field visibility
  observeScheduleChanges() {
    const observer = new MutationObserver(() => {
      const hasSchedules = document.querySelectorAll('.task-schedule-container:not(.d-none)').length > 0
      const statusSelect = document.querySelector('select[name="task[status]"]')
      
      if (hasSchedules) {
        this.hideDuedateField()
      } else if (statusSelect && statusSelect.value !== 'scheduled') {
        this.showDuedateField()
      }
    })
    
    if (this.hasSchedulesContainerTarget) {
      observer.observe(this.schedulesContainerTarget, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      })
    }
  }
  
  // Initialize schedule delete buttons
  initDeleteButtons() {
    document.querySelectorAll(".destroy-schedule-btn").forEach(button => {
      button.addEventListener("click", this.handleDeleteSchedule.bind(this))
    })
  }
  
  // Add new schedule
  addNewSchedule(event) {
    event.preventDefault()
    
    const template = document.getElementById("schedule-template")
    if (!template) return
    
    // Generate unique index and ID
    const timestamp = new Date().getTime()
    const randomPart = Math.floor(Math.random() * 10000)
    const scheduleIndex = `new_${timestamp}_${randomPart}`
    const scheduleId = `schedule-${timestamp}-${randomPart}`
    
    // Clone template
    const scheduleNode = document.importNode(template.content, true)
    const scheduleContainer = scheduleNode.querySelector(".task-schedule-container")
    scheduleContainer.id = scheduleId
    
    // Update name attributes with the specific index for this schedule
    const prefix = `task[task_schedules_attributes][${scheduleIndex}]`
    scheduleNode.querySelectorAll("[name^='SCHEDULE_PREFIX']").forEach(input => {
      const newName = input.getAttribute("name").replace("SCHEDULE_PREFIX", prefix)
      input.setAttribute("name", newName)
    })
    
    // Generate rule_name with UUID
    const ruleNameInput = scheduleNode.querySelector(".schedule-rule-name")
    if (ruleNameInput) {
      ruleNameInput.value = `task-${this.generateUuid()}`
    }
    
    // Set default schedule data
    const scheduleDataInput = scheduleNode.querySelector(".schedule-data")
    if (scheduleDataInput) {
      scheduleDataInput.value = JSON.stringify({
        frequency: { value: 1, type: "week" },
        days: { monday: ["09:00"] },
        endType: "never"
      })
    }
    
    // Update radio button IDs and names to be unique for this schedule
    scheduleNode.querySelectorAll("input[type='radio'][name='endType_SCHEDULE_INDEX']").forEach(radio => {
      // Create a unique name for the radio button group
      const newName = `endType_${scheduleIndex}`
      radio.setAttribute("name", newName)
      
      // Update the ID to be unique
      const oldId = radio.getAttribute("id")
      const newId = oldId.replace("SCHEDULE_INDEX", scheduleIndex)
      radio.setAttribute("id", newId)
      
      // Find and update corresponding label
      const label = scheduleNode.querySelector(`label[for='${oldId}']`)
      if (label) {
        label.setAttribute("for", newId)
      }
    })
    
    // Add to DOM and initialize
    this.schedulesContainerTarget.appendChild(scheduleNode)
    
    // Initialize the Stimulus controller for the new schedule
    const controller = this.application.getControllerForElementAndIdentifier(scheduleContainer, "task-schedule")
    if (controller && typeof controller.initialize === 'function') {
      controller.initialize()
    }
    
    // Re-initialize delete buttons
    this.initDeleteButtons()
    
    // Always hide due date field when schedule is added
    this.hideDuedateField()
    
    // Only disable form fields if this is an existing task (not a new task being created)
    const taskId = this.element.dataset.taskId
    const isExistingTask = taskId && taskId !== ''
    
    if (isExistingTask) {
      this.disableMainFormFields()
    }
  }
  
  // Handle schedule deletion
  handleDeleteSchedule(event) {
    const button = event.currentTarget
    const container = button.closest(".task-schedule-container")
    const destroyCheckbox = container.querySelector(".destroy-schedule-checkbox")
    
    if (destroyCheckbox) {
      destroyCheckbox.checked = true
      container.classList.add("d-none")
    } else {
      container.remove()
    }
    
    // Check if we should re-enable fields
    const remainingSchedules = document.querySelectorAll('.task-schedule-container:not(.d-none)').length
    const statusSelect = document.querySelector('select[name="task[status]"]')
    const taskId = this.element.dataset.taskId
    const isExistingTask = taskId && taskId !== ''
    
    // Show due date field if no schedules remain and status is not scheduled
    if (remainingSchedules === 0 && statusSelect && statusSelect.value !== 'scheduled') {
      this.showDuedateField()
    }
    
    // Re-enable fields if:
    // 1. Status is not scheduled, AND
    // 2. Either no schedules remain OR this is a new task being created
    if (statusSelect && statusSelect.value !== 'scheduled' && (!isExistingTask || remainingSchedules === 0)) {
      this.enableMainFormFields()
    }
  }
  
  // Generate a UUID v4 format string
  generateUuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0
      const v = c === 'x' ? r : (r & 0x3 | 0x8)
      return v.toString(16)
    })
  }

  // Initialize existing schedules
  initExistingSchedules() {
    document.querySelectorAll(".task-schedule-container").forEach(container => {
      const scheduleDataInput = container.querySelector(".schedule-data")
      if (scheduleDataInput) {
        console.log("Found existing schedule:", scheduleDataInput.value)
        
        // Initialize the controller manually if needed
        setTimeout(() => {
          const controller = this.application.getControllerForElementAndIdentifier(container, "task-schedule")
          if (controller) {
            console.log("Initializing existing schedule controller")
            if (typeof controller.initializeSchedule === 'function') {
              controller.initializeSchedule()
            } else {
              console.warn("initializeSchedule method not found on controller")
            }
          } else {
            console.warn("Could not find task-schedule controller for", container)
          }
        }, 100) // Slightly longer timeout to ensure controller is ready
      }
    })
  }
} 